// src/services/typing.js
//
// 🟢 TYPING + LIVE ACTIVITY — a pure Socket.IO relay, no Firestore, no RTDB.
//
//   emit  'start-typing' { fromUserId, fromUserName, chatId, isGroup, toUserId,
//                          participantIds, activity }
//   emit  'stop-typing'  (same payload)
//   recv  'user-typing'          { chatId, fromUserId, fromUserName, activity }
//   recv  'user-stopped-typing'  { chatId, fromUserId }
//
// 🟥 THE PHONE HAS NO RECEIVER-SIDE EXPIRY. It relies entirely on the sender emitting
//    'stop-typing' — so a peer that crashes, closes the tab, or drops off the network
//    mid-word leaves "typing…" on screen for ever. A browser tab is far more likely to
//    vanish that way than an app is, so this module adds a safety expiry the phone
//    doesn't have. It has to be GENEROUS: the app emits start-typing exactly ONCE at
//    the beginning of a burst and then only a stop after 2s of idle, so a short expiry
//    would blink the indicator off while someone is still typing a long message.
//    45s, plus two decisive clears: a message arriving from that sender, and a socket
//    disconnect. Our own outgoing typing heartbeats so no peer has to guess about us.

import { getSocket } from './socket';

const IDLE_STOP_MS = 2000;      // matches the phone's debounce
const HEARTBEAT_MS = 4000;      // ours only — keeps a long burst alive for any receiver
const EXPIRY_MS = 45000;        // safety net the phone lacks

// chatId -> uid -> { name, activity, at }
const registry = new Map();
const subs = new Set();
let wired = false;
let sweeper = null;

function snapshot() {
  const out = {};
  registry.forEach((users, chatId) => {
    const list = [];
    users.forEach((v, uid) => list.push({ uid, name: v.name, activity: v.activity }));
    if (list.length) out[chatId] = list;
  });
  return out;
}
const notify = () => { const s = snapshot(); subs.forEach((f) => { try { f(s); } catch {} }); };

function setTyper(chatId, uid, name, activity) {
  if (!chatId || !uid) return;
  if (!registry.has(chatId)) registry.set(chatId, new Map());
  registry.get(chatId).set(uid, { name: name || 'Someone', activity: activity || null, at: Date.now() });
  notify();
}
function clearTyper(chatId, uid) {
  const users = registry.get(chatId);
  if (!users || !users.delete(uid)) return;
  if (!users.size) registry.delete(chatId);
  notify();
}
/** A message from someone is proof they stopped typing — the most reliable clear there is. */
export function clearTypingFor(chatId, uid) { clearTyper(chatId, uid); }

export function watchTyping(cb) {
  subs.add(cb);
  cb(snapshot());

  if (!wired) {
    const s = getSocket();
    if (s) {
      wired = true;
      s.on('user-typing', (d) => setTyper(d?.chatId, d?.fromUserId, d?.fromUserName, d?.activity));
      s.on('chat-activity', (d) => {
        if (!d?.chatId || !d?.fromUserId) return;
        if (d.activity) setTyper(d.chatId, d.fromUserId, d.fromUserName, d.activity);
        else clearTyper(d.chatId, d.fromUserId);
      });
      s.on('user-stopped-typing', (d) => clearTyper(d?.chatId, d?.fromUserId));
      // a dropped socket means every indicator we're holding is now unverifiable
      s.on('disconnect', () => { registry.clear(); notify(); });
    }
  }
  /* 🟥 THE SWEEPER WAS ONLY EVER STARTED ON THE FIRST WIRING. The last unsubscribe
     stops it — and since `wired` stays true, the next subscriber never started it
     again, so the 45 s expiry (the net under a lost "stopped typing") was dead for
     the rest of the session and a stuck "typing…" stayed stuck. */
  if (!sweeper) {
    sweeper = setInterval(() => {
      const now = Date.now();
      let changed = false;
      registry.forEach((users, chatId) => {
        users.forEach((v, uid) => {
          if (now - v.at > EXPIRY_MS) { users.delete(uid); changed = true; }
        });
        if (!users.size) registry.delete(chatId);
      });
      if (changed) notify();
    }, 5000);
  }

  return () => {
    subs.delete(cb);
    if (!subs.size && sweeper) { clearInterval(sweeper); sweeper = null; }
  };
}

// ── publishing ────────────────────────────────────────────────────────────
function payloadFor(chat, myUid, myName) {
  const isGroup = !!chat.isGroup;
  const other = (chat.participants || []).find((u) => u && u !== myUid) || null;
  return {
    fromUserId: myUid,
    fromUserName: myName || 'Someone',
    chatId: chat.id,
    isGroup,
    toUserId: isGroup ? null : other,
    participantIds: isGroup ? (chat.participants || []) : null,
  };
}

/** Per-chat sender state, so two open chats can't stomp each other's timers. */
const senders = new Map();
function senderFor(key) {
  if (!senders.has(key)) senders.set(key, { on: false, idle: null, beat: null, activity: null });
  return senders.get(key);
}

/** Call on every keystroke (activity omitted), or with a kind while sending media. */
export function publishTyping(chat, myUid, myName, activity = null) {
  if (!chat?.id || !myUid) return;
  // note-to-self has nobody to tell, exactly like the phone
  if (!chat.isGroup && !(chat.participants || []).some((u) => u && u !== myUid)) return;
  const s = getSocket();
  if (!s) return;
  const st = senderFor(chat.id);
  const body = { ...payloadFor(chat, myUid, myName), activity: activity || undefined };

  if (!st.on || st.activity !== activity) {
    st.on = true;
    st.activity = activity;
    try { s.emit('start-typing', body); } catch {}
    if (activity) { try { s.emit('chat-activity', body); } catch {} }
    clearInterval(st.beat);
    // heartbeat so a receiver with an expiry (ours, or a future one) never times us out
    st.beat = setInterval(() => {
      try { s.emit('start-typing', body); } catch {}
      if (activity) { try { s.emit('chat-activity', body); } catch {} }
    }, HEARTBEAT_MS);
  }

  clearTimeout(st.idle);
  // a media activity is ended explicitly by the caller, not by going idle
  if (!activity) st.idle = setTimeout(() => stopTyping(chat, myUid, myName), IDLE_STOP_MS);
}

export function stopTyping(chat, myUid, myName) {
  if (!chat?.id) return;
  const st = senders.get(chat.id);
  if (!st || !st.on) return;
  st.on = false;
  st.activity = null;
  clearTimeout(st.idle);
  clearInterval(st.beat);
  const s = getSocket();
  if (!s) return;
  const body = { ...payloadFor(chat, myUid, myName), activity: null };
  try { s.emit('stop-typing', body); } catch {}
  try { s.emit('chat-activity', body); } catch {}
}

/** The phone's ACTIVITY_ICON keys — 'recording' is the only one that isn't "sending". */
export const activityVerb = (kind) => (kind === 'recording' ? 'recording' : 'sending');
